import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

// ─── Department Form Dialog (create / edit) ───────────────────────────────────

export const DepartementFormDialog = ({
  departementDialog,
  setDepartementDialog,
  newDepartement,
  setNewDepartement,
  getDepartementsParentOptions,
  employees,
  handleSaveDepartement,
}) => {
  const { t } = useTranslation();
  return (
  <AlertDialog
    open={departementDialog.open}
    onOpenChange={(open) => setDepartementDialog({ ...departementDialog, open })}
  >
    <AlertDialogContent className="max-w-2xl">
      <AlertDialogHeader>
        <AlertDialogTitle>
          {departementDialog.mode === "create" ? t("organigramme.form.createTitle") : t("organigramme.form.editTitle")}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {departementDialog.parent
            ? t("organigramme.form.addSubTo", { name: departementDialog.parent.name })
            : t("organigramme.form.createRoot")}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("organigramme.form.name")}</Label>
            <Input
              id="name"
              value={newDepartement.name}
              onChange={(e) => setNewDepartement({ ...newDepartement, name: e.target.value })}
              placeholder={t("organigramme.form.namePlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="code">{t("organigramme.form.code")}</Label>
            <Input
              id="code"
              value={newDepartement.code}
              onChange={(e) => setNewDepartement({ ...newDepartement, code: e.target.value.toUpperCase() })}
              placeholder="RH"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">{t("organigramme.form.description")}</Label>
          <Textarea
            id="description"
            value={newDepartement.description}
            onChange={(e) => setNewDepartement({ ...newDepartement, description: e.target.value })}
            placeholder={t("organigramme.form.descPlaceholder")}
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("organigramme.form.parent")}</Label>
          <Select
            value={
              newDepartement.parent_id
                ? newDepartement.parent_id.toString()
                : departementDialog.parent
                  ? departementDialog.parent.id.toString()
                  : "none"
            }
            onValueChange={(value) =>
              setNewDepartement({ ...newDepartement, parent_id: value === "none" ? null : parseInt(value) })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t("organigramme.form.selectParent")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("organigramme.form.rootOption")}</SelectItem>
              {getDepartementsParentOptions().map((dept) => (
                <SelectItem key={dept.id} value={dept.id.toString()}>
                  {"📁 " + dept.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {departementDialog.parent
              ? t("organigramme.form.parentPreselected", { name: departementDialog.parent.name })
              : t("organigramme.form.parentHint")}
          </p>
        </div>

        <div className="space-y-2">
          <Label>{t("organigramme.form.manager")}</Label>
          <Select
            value={newDepartement.manager_id || "none"}
            onValueChange={(value) =>
              setNewDepartement({ ...newDepartement, manager_id: value === "none" ? null : value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t("organigramme.form.selectManager")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("organigramme.form.noManager")}</SelectItem>
              {employees.map((employee) => (
                <SelectItem key={employee.id} value={employee.id.toString()}>
                  {employee.first_name} {employee.last_name} - {employee.job_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="max_staff">{t("organigramme.form.maxStaff")}</Label>
            <Input
              id="max_staff"
              type="number"
              value={newDepartement.max_staff}
              onChange={(e) => setNewDepartement({ ...newDepartement, max_staff: e.target.value })}
              placeholder="10"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="current_staff">{t("organigramme.form.currentStaff")}</Label>
            <Input
              id="current_staff"
              type="number"
              value={newDepartement.current_staff}
              onChange={(e) => setNewDepartement({ ...newDepartement, current_staff: e.target.value })}
              placeholder="10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="annual_budget">{t("organigramme.form.annualBudget")}</Label>
          <div className="relative">
            <Input
              id="annual_budget"
              type="number"
              value={newDepartement.annual_budget}
              onChange={(e) => setNewDepartement({ ...newDepartement, annual_budget: e.target.value })}
              placeholder="50 000"
            />
          </div>
        </div>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
        <AlertDialogAction onClick={handleSaveDepartement}>
          {departementDialog.mode === "create" ? t('common.create', 'Create') : t('common.edit', 'Edit')}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  );
};

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

export const DeleteDepartementDialog = ({
  deleteDialog,
  setDeleteDialog,
  handleDeleteDepartement,
}) => {
  const { t } = useTranslation();
  return (
  <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{t('organigramme.deleteDept.title')}</AlertDialogTitle>
        <AlertDialogDescription>
          {t('organigramme.deleteDept.confirm', { name: deleteDialog.departement?.name })}
          {deleteDialog.departement?.enfants?.length > 0 && (
            <span className="block mt-2 text-red-600">
              {t('organigramme.deleteDept.warning')}
            </span>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
        <AlertDialogAction
          onClick={() => handleDeleteDepartement(deleteDialog.departement)}
          className="bg-destructive hover:bg-destructive/90"
        >
          {t('common.delete', 'Delete')}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  );
};
